/**
 * Statusmapping for brukervisningen: fra motorens utfall (+ eventuell
 * opplastet dokumentasjon) til den statusen søkeren ser.
 *
 * Ren logikk, ingen JSX og ingen kobling til søknadens tilstand. Modulen er
 * portert ordrett fra kraveditoren (SertSlay); eneste endring er at
 * `normalizeStatus` hentes fra klientens egen `labels.ts`.
 */

import { normalizeStatus } from '../labels';

/**
 * Statusene søkeren ser.
 *
 * - `fulfilled` (grønn) — kravet er kontrollert og godkjent.
 * - `uploaded` (grønn, som `fulfilled`) — dokumentasjon er mottatt, men ikke
 *   kontrollert ennå. Statusene deler farge; ansiktet og ordet skiller dem.
 * - `missing` (warning) — kravet står igjen; brukeren må gjøre noe.
 * - `notApplicable` (grå) — kravet inngår ikke i kvalifiseringsveien brukeren
 *   benytter (et alternativ brukeren ikke følger).
 *
 * Det finnes ingen egen «uavklart»-status. Motorens `Inconclusive` er ikke noe
 * søkeren kan handle på — og det som oftest løser et uavklart krav er nettopp
 * at hun laster opp dokumentasjon — så den vises som «Mangler» sammen med
 * `Fail`. Skillet mellom de to lever fortsatt i motorens eget svar, og bæres
 * videre i `engineStatus` på noden.
 */
export type UserStatus = 'fulfilled' | 'uploaded' | 'missing' | 'notApplicable';

/**
 * Alvorlighetsrangering. Brukes av begge aggregatene: «alle må oppfylles» tar
 * den verste, «minst én må oppfylles» tar den beste. `notApplicable` er 0 og
 * deltar aldri — den settes som et etterpass.
 */
const SEVERITY: { [key in UserStatus]: number } = {
  notApplicable: 0,
  fulfilled: 1,
  uploaded: 2,
  missing: 3,
};

/** Statuser som betyr at brukeren må gjøre noe. */
export const isOutstanding = (status: UserStatus): boolean => status === 'missing';

/**
 * Opplasting lagt oppå en status.
 *
 * En status som venter på brukeren blir «Dokumentert»; alt annet blir
 * stående. Et oppfylt krav forblir grønt — dokumentasjon kan ikke gjøre et
 * godkjent krav til noe annet — og et uaktuelt krav er fortsatt uaktuelt.
 *
 * Regelen er den samme enten opplastingen ligger på et enkeltkrav eller på en
 * hel gruppe som dokumenteres under ett, og står derfor for seg selv.
 */
export const withUpload = (status: UserStatus, hasUpload: boolean): UserStatus =>
  hasUpload && isOutstanding(status) ? 'uploaded' : status;

/**
 * Motorstatus + opplastet dokumentasjon → brukerstatus.
 *
 * Et krav motoren har godkjent er grønt uansett. Alt annet — `Fail`,
 * `Inconclusive` og ukjente verdier — er «Mangler»: det står igjen noe, og
 * veien videre er den samme uansett hvorfor. Opplastingen løfter det til
 * «Dokumentert»: dokumentasjonen er mottatt, men ikke kontrollert.
 */
export const mapEngineStatus = (engineStatus: unknown, hasUpload = false): UserStatus => {
  const engine = normalizeStatus(engineStatus);
  if (engine === 'pass') return 'fulfilled';
  return withUpload('missing', hasUpload);
};

/** «Alle må være oppfylt» — den verste statusen vinner. */
export const aggregateAll = (statuses: UserStatus[]): UserStatus | undefined => {
  const relevant = statuses.filter((status) => status !== 'notApplicable');
  if (relevant.length === 0) return undefined;

  return relevant.reduce((worst, status) => (SEVERITY[status] > SEVERITY[worst] ? status : worst));
};

/** «Minst én må være oppfylt» — den beste statusen vinner. */
export const aggregateAny = (statuses: UserStatus[]): UserStatus | undefined => {
  const relevant = statuses.filter((status) => status !== 'notApplicable');
  if (relevant.length === 0) return undefined;

  return relevant.reduce((best, status) => (SEVERITY[status] < SEVERITY[best] ? status : best));
};
